
import {
  DefaultMapCoordinates,
  googleMapsApiKey,
  ICoordinates,
  IVacancy,
  IVacancyMapPoint,
  VacancyMapPointGetters
} from '@tokyojob/core'
import debounce from 'lodash.debounce'
import Vue, { PropType } from 'vue'
import { ValidationProvider } from 'vee-validate'
import { GoogleMapWrapper, MarkerIconFactory } from '@tokyojob/frontend-components'
import MarkerClusterer from '@googlemaps/markerclustererplus'
import CVacancyCardSmallMapComponent from '@/modules/auth/components/candidate/c-vacancy-card-small-map/c-vacancy-card-small-map.component.vue'
import { vacancyCardSmallMapUpdatedEvent } from '../c-vacancy-card-small-map/constants/events'

// Events
const AutocompletePlaceChangedEvent = 'place_changed'
const BoundsChangedEvent = 'bounds_changed'
const DragEndEvent = 'dragend'
const ZoomChanged = 'zoom_changed'

// Configs
const debounceDuration = 300
const defaultZoomLevel = 11

// Google Map Wrapper
const googleMapsWrapper = new GoogleMapWrapper()

// IData
interface IData {
  /**
   * Reference to the created map
   */
  map?: google.maps.Map
  /**
   * Reference to the markers on the map
   */
  markers: Dictionary<google.maps.Marker>
  /**
   * Reference to the center marker of the map
   */
  centerMarker?: google.maps.Marker
  /**
   * Reference to the map's information window
   */
  infoWindow?: google.maps.InfoWindow
  /**
   * Reference to the marker's clusterer
   */
  clusterer?: MarkerClusterer
  /**
   * The last position value that triggered the API call
   */
  lastPositionTrigger?: google.maps.LatLng
  /**
   * The flag for when the markers can be created
   */
  canCreateMarkers: boolean
  /**
   * The flag to whether block the map bounds event or not.
   */
  blockMapBoundsChangedEvent: boolean
  /**
   * The currently selected marker, if any.
   */
  selectedMarker?: google.maps.Marker
}

/**
 * Google maps component. Replacement for the old maps.vue component, removes all Buefy components.
 * This is the google maps package that was used: https://www.npmjs.com/package/@googlemaps/js-api-loader
 * This is the cluster package that was used: https://github.com/googlemaps/js-markerclustererplus
 * If the google.map is not working correctly kindly refer to the links below:
 * https://developers.google.com/maps/documentation/javascript/overview#Inline
 * https://developers.google.com/maps/documentation/javascript/using-typescript
 */
export default Vue.extend({
  components: {
    ValidationProvider
  },
  props: {
    /**
     * Initial central location of the map.
     */
    coordinates: {
      required: true,
      type: Object as PropType<ICoordinates>
    },
    /**
     * Coordinates of the user's current location.
     */
    currentLocationCoordinates: {
      required: false,
      type: Object as PropType<ICoordinates>,
      default: undefined
    },
    /**
     * Name of the html element in which the map should be displayed. Also used
     * as part of the name of the autocomplete field.
     */
    mapId: {
      required: true,
      type: String
    },
    /**
     * Points in the map that contain vacancies.
     */
    vacancyMapPoints: {
      required: true,
      type: Array as PropType<IVacancyMapPoint[]>
    },
    /**
     * Helps determine some of the map's settings, like zoom limits.
     */
    isMobile: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  async mounted() {
    await googleMapsWrapper.initialize(googleMapsApiKey)
    this.onMapLoaded()
  },
  data(): IData {
    return {
      map: undefined,
      markers: {},
      infoWindow: undefined,
      lastPositionTrigger: undefined,
      canCreateMarkers: false,
      blockMapBoundsChangedEvent: false
    }
  },
  watch: {
    /**
     * When coordinates is changed, set internal value.
     */
    coordinates(value: ICoordinates) {
      const coordinates = googleMapsWrapper.determineCoordinates(value)
      this.map!.setCenter(coordinates)
      // this.map!.setZoom(defaultZoomLevel)
    },
    /**
     * Triggers a map center update
     */
    currentLocationCoordinates(value: ICoordinates) {
      this.updateCenterMapMarker(value)
    },
    /**
     * Updates the vacancy markers.
     */
    vacancyMapPoints(vacancyMapPoints: IVacancyMapPoint[]) {
      this.updateMapMarkers(vacancyMapPoints)
    }
  },
  methods: {
    /**
     *
     */
    updateMapMarkers(vacancyMapPoints: IVacancyMapPoint[]): void {
      if (!this.canCreateMarkers) return
      const count = vacancyMapPoints.length
      //Let's start with all keys as unused :)
      const unusedKeys = Object.keys(this.markers)
      for (let i = 0; i < count; ++i) {
        const item = vacancyMapPoints[i]
        //Key is based on the coordinates of the vacancy group :)
        const key = VacancyMapPointGetters.getCoordinateStr(item)!
        //Get the marker from this.markers using the key above
        let marker = this.markers[key]
        //If the key doesn't exist in the this.markers this means that the coordinates is new
        if (!marker) {
          marker = googleMapsWrapper.createMarker(this.map!, item.coordinates)
          ;(marker as any).customData = { point: item }
          marker.addListener('click', () => {
            this.onMarkerClicked(marker, (marker as any).customData.point)
          })
          this.markers[key] = marker
          //Else it is existing already and we just need to update the vacancy content of the marker
        } else {
          marker.setVisible(true)
          ;(marker as any).customData = { point: item }
        }
        //Of course since the key was used we remove it to the unusedKeys array which will be used later
        const index = unusedKeys.findIndex((e) => e === key)
        unusedKeys.splice(index, 1)
      }
      //These are the unused keys in the this.markers which means they are filtered out already
      unusedKeys.forEach((e) => {
        this.markers[e].setVisible(false)
      })
      this.clusterer!.clearMarkers()
      const filteredMarkers = Object.values(this.markers).filter((e) => e.getVisible())
      this.clusterer!.addMarkers(filteredMarkers)
    },
    /**
     * Setup the center marker
     */
    updateCenterMapMarker(coordinates: ICoordinates): void {
      if (coordinates.lat === DefaultMapCoordinates.lat && coordinates.lng === DefaultMapCoordinates.lng) return
      if (!this.centerMarker) this.centerMarker = googleMapsWrapper.createCenterMarker(this.map!, coordinates)
      else this.centerMarker.setPosition(coordinates)
    },
    /**
     * When an autocomplete value is selected, gets the location information and emits
     * update events.
     */
    updateCoordinatesOnAutocomplete(autocomplete: google.maps.places.Autocomplete): void {
      const place = autocomplete.getPlace()
      if (place && place.geometry) {
        const location = place.geometry.location
        const coordinates = {
          lat: location!.lat(),
          lng: location!.lng()
        } as ICoordinates
        this.$emit('updateCenterCoordinates', coordinates)
      }
    },
    mapBoundsChanged(map: google.maps.Map): void {
      // We can't remove listeners when we add them :( so no choice but to block them instead
      if (this.blockMapBoundsChangedEvent) return
      this.mapDragEnd(map)
      this.blockMapBoundsChangedEvent = true
    },
    /**
     * Called whenever the bounds of the map changed
     */
    mapDragEnd(map: google.maps.Map): void {
      const mapBounds = map.getBounds()!
      // We get the map's current bounds and traverse through all of our markers and set it's visibility
      const markers = Object.values(this.markers)
      markers.forEach((e) => {
        const position = e.getPosition()!
        const isVisible = mapBounds?.contains(position)!
        e.setVisible(isVisible)
      })
      // We will be updating the map bounds related query string in the URL
      const southWest = mapBounds.getSouthWest()
      const northEast = mapBounds.getNorthEast()
      const lowerLeftLng = southWest.lng().toString()
      const lowerLeftLat = southWest.lat().toString()
      const upperRightLng = northEast.lng().toString()
      const upperRightLat = northEast.lat().toString()
      const currentQuery = {
        ...this.$route.query,
        lowerLeftLng,
        lowerLeftLat,
        upperRightLng,
        upperRightLat
      }
      if (JSON.stringify(this.$route.query) === JSON.stringify(currentQuery)) return
      this.$router.replace({ query: currentQuery })
    },
    /**
     * Convert IVacancyMapPoint coords to a string to use as key in markers object
     */
    getMarkerKeyFromCoords(item: IVacancyMapPoint) {
      const key = VacancyMapPointGetters.getCoordinateStr(item)!
      //Get the marker from this.markers using the key above
      return this.markers[key]
    },
    /**
     * Use the coordinates to find the current marker from Dict<google.maps.Marker>
     */
    setSelectedMarker(item: IVacancyMapPoint) {
      let marker = this.getMarkerKeyFromCoords(item)
      this.selectedMarker = marker
      this.updateMarkersIcon()
    },
    updateMarkersIcon() {
      for(const key in this.markers) {
        const marker = this.markers[key]
        marker.setIcon(marker === this.selectedMarker ? MarkerIconFactory.selectedMarkerIcon() : MarkerIconFactory.defaultMarkerIcon())
      }
    },
    /**
     * This is called everytime a marker is clicked
     */
    onMarkerClicked(marker: google.maps.Marker, point: IVacancyMapPoint): void {
      this.createInfoWindow(point, marker, 0)
      this.setSelectedMarker(point)
      this.map!.panTo(marker.getPosition()!)
      this.panCardToCentre()
      this.$emit('markerClicked', marker)
    },
    /**
     * Centers the map in a marker and opens its info window.
     * @todo Change marker when selected
     */
    highlightMarker(item: IVacancy, point: IVacancyMapPoint): void {
      const key = VacancyMapPointGetters.getCoordinateStr(point)!
      const marker = this.markers[key]
      if (!marker) return
      const { lat, lng } = point.coordinates
      // If I do not convert to str and back to float, the browser throws an error :(
      this.map!.setCenter({ lat: parseFloat(lat.toString()), lng: parseFloat(lng.toString()) })
      const zoom = googleMapsWrapper.determineMapZoom(point.coordinates)
      this.map!.setZoom(zoom)
      this.panCardToCentre()
      const vacancyIndex = point.vacancies.findIndex((e) => e.id === item.id)
      this.setSelectedMarker(point)
      this.createInfoWindow(point, marker, vacancyIndex > -1 ? vacancyIndex : 0)
    },
    /**
     * Initialization of this component
     */
    onMapLoaded(): void {
      const coordinates = googleMapsWrapper.determineCoordinates(this.coordinates)
      const mapOptions = googleMapsWrapper.getMapOptions(this.isMobile)
      this.map = googleMapsWrapper.createMap(this.mapId, coordinates, mapOptions)
      this.mapListenersSetup(this.map)
      const autocomplete = googleMapsWrapper.createAutocomplete(this.mapId)
      this.autocompleteListenersSetup(autocomplete)
      this.infoWindow = googleMapsWrapper.createInfoWindow()
      this.clusterer = googleMapsWrapper.createMarkerClusterer(this.map, this.clusterCalculator)
      this.canCreateMarkers = true
      this.lastPositionTrigger = this.map.getCenter()
      this.updateMapMarkers(this.vacancyMapPoints)
    },
    /**
     * Sets up the autocomplete listeners. If the autocomplete control does not exist, no setup occurs.
     */
    autocompleteListenersSetup(autocomplete?: google.maps.places.Autocomplete): void {
      if (!autocomplete) return
      autocomplete.addListener(AutocompletePlaceChangedEvent, () => {
        this.updateCoordinatesOnAutocomplete(autocomplete)
      })
    },
    /**
     * Sets up the map lsiteners used to update the displayed vacancies.
     */
    mapListenersSetup(map: google.maps.Map): void {
      const debouncedCallback = debounce((mapBoundsUpdated: Function) => {
        mapBoundsUpdated()
      }, debounceDuration)
      map.addListener(BoundsChangedEvent, () => this.mapBoundsChanged(this.map!))
      map.addListener(DragEndEvent, () => debouncedCallback(() => this.mapDragEnd(this.map!)))
      // TODO: Adding a listener on zoom causes the info window (if selected) to disappear
      map.addListener(ZoomChanged, () => debouncedCallback(() => this.mapDragEnd(this.map!)))
    },
    /**
     * The calculator for the cluster. Defines the number label of the cluter.
     */
    clusterCalculator(markers: google.maps.Marker[], numStyles: number) {
      let vacancyCounts = 0
      markers.forEach((e) => {
        vacancyCounts += (e as any).customData.point.vacancies.length
      })
      const index = numStyles
      const text = vacancyCounts.toString()
      const title = ''
      return {
        index,
        text,
        title
      }
    },
    /**
     * Creates an info window that contains the vacancy card.
     */
    createInfoWindow(point: IVacancyMapPoint, marker: google.maps.Marker, vacancyIndex: number) {
      const router = this.$router
      const InfoWindowContentConstructor = Vue.extend(CVacancyCardSmallMapComponent)
      const CVacancyCardSmallMap = new InfoWindowContentConstructor({
        router,
        propsData: {
          vacancies: point.vacancies,
          currentVacancyPage: vacancyIndex
        }
      })
      CVacancyCardSmallMap.$mount()
      this.infoWindow!.setContent(CVacancyCardSmallMap.$el)
      this.infoWindow!.open(this.map, marker)
      this.$emit('openInfoWindow')
      CVacancyCardSmallMap.$on('close', () => {
        this.infoWindow?.close()
        this.$emit('closeInfoWindow')
        marker.setIcon(MarkerIconFactory.defaultMarkerIcon())
        this.map!.setZoom(13)
      })
      const event = vacancyCardSmallMapUpdatedEvent
      CVacancyCardSmallMap.$on(event, (vacancy: IVacancy) => this.$emit(event, vacancy))
    },
    /**
     * Pan the map down 200px when a card or marker is clicked, to make the card center screen
     */
    panCardToCentre() {
      this.map!.panBy(0, -200)
    }
  }
})
